import { Fragment, useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';
import { Ledger, ledgerStatusList, ledgerPaymentStatusList } from 'model/Ledger';
import { Report } from 'model/Report';
import { generalStyle } from 'helper/Style';
import { convertDate, convertDateCode } from 'helper/Converter';
import Layout from 'component/part/Layout';
import List from 'component/part/List';
import BackLink from 'component/part/BackLink';
import H2 from 'component/part/H2';
import SelectDate from 'component/part/SelectDate';
import FormContent from 'component/part/FormContent';

type ListLedger = Ledger & {
  leaderName: string;
  statusLabel: string;
  paymentStatusLabel: string;
  endDateTime: string;
  assigned: boolean;
  totalCost: number;
};

const ledgerConditions = { with: ['cost'] };

const ledgerIndexes = [
  { label: '現場名略称', key: 'shortName' },
  { label: '工事内容' },
  { label: '工期（終了年月日）', key: 'endDate' },
  { label: '現場代理人', key: 'leaderId' },
  { label: '人員手配' },
  { label: 'ステータス', key: 'status' },
  { label: '総経費' },
  { label: '請求ステータス', key: 'paymentStatus' },
];

const LedgerList: React.FC = () => {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const urlParams = useParams<{ date: string }>();
  const { state } = useDataContext();
  const { auth: authState } = state || {};
  const user = authState?.user;
  const { list = [] } = state?.ledger || {};
  const api = useApi();
  const [ledgers, setLedgers] = useState<ListLedger[]>([]);
  const [date, setDate] = useState<Date>(tomorrow);
  const [dateReports, setDateReports] = useState<{ [dateCode: number]: Report[] }>({});
  const dateCode = convertDateCode(date);

  useEffect(() => {
    (async () => {
      const [usersResponse, reportsResponse] = await Promise.all([
        api.user.getsByOwn(),
        api.report.getsByOwn({ dateCode }),
      ]);
      const listLedgers: ListLedger[] = list.map((ledger) => {
        const leader = usersResponse.find((user) => user.id === ledger.leaderId);
        const status = ledgerStatusList.find(({ value }) => value === ledger.status) || ledgerStatusList[0];
        const paymentStatus = ledgerPaymentStatusList.find(({ value }) => value === ledger.paymentStatus) || ledgerPaymentStatusList[0];
        const totalCost = (ledger.costs || []).reduce((value, { cost }) => value + cost, 0);

        return {
          ...ledger,
          leaderName: leader ? `${leader.lastName} ${leader.firstName}` : '',
          statusLabel: status.label,
          paymentStatusLabel: paymentStatus.label,
          endDateTime: convertDate(ledger.endDate),
          assigned: !!reportsResponse.find(({ ledgerId }) => ledgerId === ledger.id),
          totalCost,
        };
      });
      setLedgers(listLedgers);
      setDateReports({ ...dateReports, [dateCode]: reportsResponse });
    })();
  }, [list]);

  useEffect(() => {
    (async () => {
      if (!(dateCode in dateReports)) {
        const reportsResponse = await api.report.getsByOwn({ dateCode });
        dateReports[dateCode] = reportsResponse;
        setDateReports({ ...dateReports });
      }
      const reports = dateReports[dateCode] || [];
      if (ledgers.length) {
        ledgers.forEach((ledger) => {
          ledger.assigned = !!reports.find(({ ledgerId }) => ledgerId === ledger.id);
        });
        setLedgers([...ledgers]);
        window.history.pushState(null, '', `/ledger/list/${dateCode}`);
      }
    })();
  }, [date]);

  useEffect(() => {
  const initialDateCode = parseInt(urlParams.date || '0');
  if (initialDateCode) {
    const year = Math.floor(initialDateCode / 10000);
    const month = Math.floor((initialDateCode - year * 10000) / 100);
    const day = initialDateCode % 100;
    const initialDate = new Date(year, month - 1, day);
    setDate(initialDate);
  }
  }, []);

  return <Layout>
    <Fragment>
      {!user?.role?.permissions.ledgerEdit && (
        <BackLink link='/' label='トップ'></BackLink>
      )}
    </Fragment>
    <H2>台帳一覧</H2>
    <div css={generalStyle.form}>
      <div css={generalStyle.formRow}>
        <FormContent label='作業日'>
          <SelectDate
            onChange={(value) => value && setDate(value)}
            selected={date}
            minDate={today}
          />
        </FormContent>
      </div>
      <List
        type='ledger'
        conditions={ledgerConditions}
        indexes={ledgerIndexes}
        css={generalStyle.list(8, { right: [7] })}
      >
        {ledgers.map((ledger, index) => (
          <div css={generalStyle.listRow} key={index}>
            <div>
              <NavLink to={`/ledger/${ledger.id}`} css={generalStyle.link}>{ledger.shortName}</NavLink>
            </div>
            <div>{ledger.summary}</div>
            <div>{ledger.endDateTime}</div>
            <div>{ledger.leaderName}</div>
            <div>
              {
                ledger.assigned ? (
                  <div>手配済み</div>
                ) : (
                  <NavLink to={`/arrangement/${ledger.id}/${dateCode}`} css={generalStyle.button()}>人員手配</NavLink>
                )
              }
            </div>
            <div>{ledger.statusLabel}</div>
            <div>&yen;{(ledger.totalCost || 0).toLocaleString()}</div>
            <div>{ledger.paymentStatusLabel}</div>
          </div>
        ))}
      </List>
    </div>
  </Layout>;
};

export default LedgerList;
